import { AppBar, Box, Dialog, IconButton, Paper, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { BoardLayout, PublicResultsLayout } from '../../types/domain';
import { BOARD_LAYOUT_LABELS, PUBLIC_LAYOUT_LABELS } from '../../types/domain';
import { ResultsTable } from '../results/ResultsTable';
import { PodiumResultsView } from '../results/PodiumResultsView';
import { SplitView } from '../results/SplitView';
import { MatrixBoard } from '../board/MatrixBoard';
import { FocusBoard } from '../board/FocusBoard';
import { CarouselBoard } from '../board/CarouselBoard';
import { SplitBoard } from '../board/SplitBoard';
import { ColumnsBoard } from '../board/ColumnsBoard';
import {
  MOCK_PUBLIC_URL,
  mockBoardCompetition,
  mockDisplayConfig,
  mockOverall,
  mockPublicCompetition,
  mockResults,
} from './mockCompetitionData';

type PublicProps = {
  open: boolean;
  onClose: () => void;
  kind: 'public';
  layout: PublicResultsLayout;
};

type BoardProps = {
  open: boolean;
  onClose: () => void;
  kind: 'board';
  layout: BoardLayout;
};

type Props = PublicProps | BoardProps;

export const LayoutPreviewDialog = (props: Props) => {
  const { open, onClose } = props;
  const title =
    props.kind === 'public'
      ? `Ukázka: ${PUBLIC_LAYOUT_LABELS[props.layout]}`
      : `Ukázka: ${BOARD_LAYOUT_LABELS[props.layout]}`;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
            Fiktivní data — pouze pro náhled
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="Zavřít ukázku">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100%', p: { xs: 1, md: 3 } }}>
        {props.kind === 'public' ? <PublicPreview layout={props.layout} /> : <BoardPreview layout={props.layout} />}
      </Box>
    </Dialog>
  );
};

const PublicPreview = ({ layout }: { layout: PublicResultsLayout }) => {
  const discipline = mockPublicCompetition.categories[0].disciplines[0];
  const category = mockPublicCompetition.categories[0];
  const disciplineResults = mockResults.filter((r) => r.disciplineId === discipline.id);
  const config = { ...mockDisplayConfig, layout };
  const overallForCategory = mockOverall.find((o) => o.categoryId === category.id);

  if (layout === 'PODIUM') {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <PodiumResultsView results={disciplineResults} config={config} showAttempt timeFormat={discipline.timeFormat} />
      </Paper>
    );
  }
  if (layout === 'SPLIT_VIEW') {
    return (
      <SplitView
        competition={mockPublicCompetition}
        config={config}
        results={disciplineResults}
        discipline={discipline}
        category={category}
        overallForCategory={overallForCategory}
      />
    );
  }
  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <ResultsTable
        results={disciplineResults}
        showAttempt
        showStartNumber={config.showStartNumber}
        showAttemptDetails={config.showAttemptDetails}
        showLaneDetails={config.showLaneDetails}
        timeFormat={discipline.timeFormat}
      />
    </Paper>
  );
};

const BoardPreview = ({ layout }: { layout: BoardLayout }) => {
  const common = {
    competition: mockBoardCompetition,
    results: mockResults,
    overall: mockOverall,
    allTeams: undefined,
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {layout === 'MATRIX' && (
        <MatrixBoard {...common} shortCode={mockBoardCompetition.shortCode} publicUrl={MOCK_PUBLIC_URL} />
      )}
      {layout === 'FOCUS' && <FocusBoard {...common} />}
      {layout === 'CAROUSEL' && <CarouselBoard {...common} />}
      {layout === 'SPLIT' && <SplitBoard {...common} />}
      {layout === 'COLUMNS' && <ColumnsBoard {...common} />}
    </Box>
  );
};
